import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Zap, Check, Send, Gamepad2, Layers, Cpu,
  ArrowLeft, Users, BrainCircuit, Terminal, Trophy
} from 'lucide-react';
import { Button } from './components/Button';
import { supabase } from './supabaseClient';

export default function Wishlist() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // State for stats and form
  const [engineStats, setEngineStats] = useState({});
  const [featureStats, setFeatureStats] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    engines: [],
    features: [] // Will store exactly one ID
  });

  const engineOptions = [
    { id: 'unity', name: 'Unity', icon: <Gamepad2 size={18} /> },
    { id: 'unreal', name: 'Unreal', icon: <Layers size={18} /> },
    { id: 'godot', name: 'Godot', icon: <Cpu size={18} /> }
  ];

  const featureOptions = [
    {
        id: 'director',
        name: 'Project Director',
        desc: 'Progress and productivity tracking with game concept rating, GDD, milestones & achievements',
        icon: <BrainCircuit size={18} />
    },
    {
        id: 'worker',
        name: 'Workspace Worker',
        desc: 'Autonomous agent for editor actions, code & assets',
        icon: <Terminal size={18} />
    }
  ];

  const formatVotes = (num) => {
    if (!num) return '0';
    if (num >= 1000) return (num / 1000).toFixed(1).replace(/\.0$/, '') + 'k';
    return num.toString();
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const { data: eData } = await supabase.rpc('get_wishlist_stats', { category_column: 'engines' });
    const { data: fData } = await supabase.rpc('get_wishlist_stats', { category_column: 'features' });

    if (eData) {
      const eObj = {}; eData.forEach(i => eObj[i.item_name] = i.vote_count);
      setEngineStats(eObj);
    }
    if (fData) {
      const fObj = {}; fData.forEach(i => fObj[i.item_name] = i.vote_count);
      setFeatureStats(fObj);
    }
  };

  const handleSelection = (field, id) => {
    setFormData(prev => {
      if (field === 'features') {
        // SINGLE SELECT LOGIC: Replace the array with just the new ID
        return { ...prev, features: [id] };
      }
      // MULTI SELECT LOGIC: For Engines
      return {
        ...prev,
        engines: prev.engines.includes(id)
          ? prev.engines.filter(item => item !== id)
          : [...prev.engines, id]
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.engines.length === 0 || formData.features.length === 0) {
        return alert("Please select at least one engine and your most anticipated feature!");
    }

    setLoading(true);
    try {
      const { error } = await supabase.from('wishlist').insert([formData]);
      if (error) throw error;
      setSubmitted(true);
      fetchData();
    } catch (err) {
      alert(err.code === '23505' ? "Already on the list!" : "Submission error.");
    } finally {
      setLoading(false);
    }
  };

  const totalWaiting = Object.values(engineStats).reduce((a, b) => a + parseInt(b || 0), 0);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200 font-sans">
      <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-950/80 backdrop-blur-md border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
            <img src="/logo512.png" alt="Logo" className="w-8 h-8 rounded-lg" />
            <span className="font-bold text-xl text-white">Hollowpad</span>
          </div>
          <Button onClick={() => navigate(-1)} className="bg-slate-900 border border-slate-800 text-white rounded-full px-4 py-2 flex items-center gap-2 text-xs uppercase tracking-widest font-bold">
             <ArrowLeft size={14} /> Back
          </Button>
        </div>
      </nav>

      <section className="pt-32 pb-20 px-6 relative flex flex-col items-center">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-indigo-600/10 rounded-full blur-[120px] -z-10" />

        <div className="max-w-xl w-full bg-slate-900/40 border border-slate-800 p-8 rounded-[2.5rem] backdrop-blur-xl shadow-2xl relative border-t-slate-700/50">
          
          <div className="absolute -top-4 -right-2 bg-blue-600 text-white text-[10px] font-black px-3 py-1 rounded-full shadow-lg flex items-center gap-1 uppercase tracking-widest border border-blue-400/50">
            <Users size={12} /> {formatVotes(totalWaiting)} Engineers Waiting
          </div>

          {!submitted ? (
            <form onSubmit={handleSubmit} className="space-y-8">
              <header>
                <h1 className="text-3xl font-black text-white mb-2 tracking-tight">Join the expansion.</h1>
                <p className="text-slate-500 text-sm">
                  Tell us what tool you are most excited for and where you want it next.
                </p>
              </header>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                    <input required className="bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 focus:border-blue-500 outline-none text-sm transition-all text-white" placeholder="Name" onChange={e => setFormData({...formData, name: e.target.value})}/>
                    <input required type="email" className="bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 focus:border-blue-500 outline-none text-sm transition-all text-white" placeholder="Email" onChange={e => setFormData({...formData, email: e.target.value})}/>
                </div>
              </div>

              {/* ENGINE SELECTION (MULTI) */}
              <div className="space-y-3">
                <label className="text-[10px] font-black text-slate-500 tracking-[0.2em] uppercase">1. Choose your engine(s)</label>
                <div className="grid grid-cols-3 gap-2">
                  {engineOptions.map((e) => (
                    <button key={e.id} type="button" onClick={() => handleSelection('engines', e.id)}
         className={`relative flex flex-col items-center gap-2 p-4 rounded-xl border transition-all ${formData.engines.includes(e.id) ? 'bg-blue-600 border-blue-400 text-white' : 'bg-slate-950 border-slate-800 text-slate-500'}`}>
                      
         
                      <span className={`absolute top-1.5 right-1.5 text-[7px] font-mono font-bold tracking-tighter uppercase ${formData.engines.includes(e.id) ? 'text-blue-200' : 'text-slate-700'}`}>
                        {formatVotes(engineStats[e.id])} VOTES
                      </span>

          {e.icon} <span
	// className="text-[10px] font-bold"
	className="text-[13px] font-bold"
			  >{e.name}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* FEATURE SELECTION (SINGLE) */}
              <div className="space-y-3">
                <label className="text-[10px] font-black text-slate-500 tracking-[0.2em] uppercase">2. Most anticipated feature?</label>
                <div className="grid grid-cols-1 gap-3">
                  {featureOptions.map((f) => (
                    <button key={f.id} type="button" onClick={() => handleSelection('features', f.id)}
                      className={`flex items-start gap-4 p-4 rounded-xl border text-left transition-all ${formData.features.includes(f.id) ? 'bg-indigo-600 border-indigo-400 text-white shadow-[0_0_20px_rgba(79,70,229,0.2)]' : 'bg-slate-950 border-slate-800 text-slate-400'}`}>
                      <div className={`p-2 rounded-lg ${formData.features.includes(f.id) ? 'bg-indigo-500' : 'bg-slate-900'}`}>{f.icon}</div>
                      <div className="flex-1">
                        <div className="flex justify-between items-center">
			  {/* <span className="text-xs opacity-95 font-black uppercase tracking-wide">{f.name}</span> */}
	 <span className="text-[17px] opacity-95 font-black uppercase tracking-wide">{f.name}</span>

			  {/* <span className="text-[9px] font-mono opacity-60 uppercase">{formatVotes(featureStats[f.id])} VOTES</span> */}
			  <span className="text-[9px] font-mono opacity-80 uppercase">{formatVotes(featureStats[f.id])} VOTES</span>
                        </div>
                        <p className={`text-[15px] leading-relaxed mt-1 ${formData.features.includes(f.id) ? 'text-indigo-100' : 'text-slate-500'}`}>{f.desc}</p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              <Button type="submit" disabled={loading} 
                  className="w-full bg-white hover:bg-blue-50 text-slate-950 py-4 rounded-xl font-black text-sm tracking-widest uppercase transition-transform active:scale-95"
                  >
                {loading ? "Registering..." : "Secure Early Access"}
              </Button>
            </form>
          ) : (
            <div className="py-12 text-center animate-in fade-in zoom-in duration-500">
              <div className="w-20 h-20 bg-blue-500/10 rounded-full flex items-center justify-center mx-auto mb-6 border border-blue-500/20 text-blue-400">
                <Trophy size={32} />
              </div>
              <h2 className="text-2xl font-black text-white uppercase tracking-tight">You're in.</h2>
              <p className="text-slate-500 text-sm mt-2 px-6">
                We've noted your interest in the <strong>{formData.features.includes('director') ? 'Project Director' : 'Workspace Worker'}</strong>. We'll be in touch soon.
              </p>
              <button onClick={() => navigate('/')} className="mt-8 text-blue-500 text-[10px] font-black uppercase tracking-widest hover:text-blue-400 transition-colors">Return to base</button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
